import React from "react";
import apiService from "../../../services/ApiService";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";

export default function SensitivityStringField({ name, item, field, currentSensitivity }) {
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache — do NOT invalidate SCENARIO_TREE_KEY (see useUpdateFieldValue.ts)
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };

    const sensitivityField = item.sensitivityModelData.fields[field.name];

    const originalValue = (field.currentValue.value) ? field.currentValue.value : "";
    const initialValue = (sensitivityField) ? sensitivityField.value.value : originalValue

    const [fieldValue, setFieldValue] = React.useState(initialValue as string);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();

    React.useEffect(() => {
        const newSensitivityField = item.sensitivityModelData.fields[field.name];
        const newOriginalValue = (field.currentValue.value) ? field.currentValue.value : "";
        const newValue = (newSensitivityField) ? newSensitivityField.value.value : newOriginalValue;
        setFieldValue(newValue as string);
    }, [item.id, field.name, field.currentValue?.value]);

    const updateValue = (value) => {
        triggerSave();
        apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, value !== "" ? value : null).then(
            async(response) => {
                if (response) {
                    const updatedField = await response.json()
                    item.sensitivityModelData.fields[field.name] = updatedField
                    resolveSave();
                    invalidateQueries();
                }
            }
        )
        setFieldValue(value);
    }

    const resetSensitivityValue = () => {
        apiService.deleteSensitivityValue(sensitivityField.id).then(
            async(response) => {
                if (response) {
                    delete item.sensitivityModelData.fields[field.name];
                    setFieldValue(originalValue)
                    invalidateQueries();
                }
            }
        )

    }

    return (
        <div className="flex flex-row items-center">
            <button
                onClick={resetSensitivityValue}
                disabled={!sensitivityField}
                className="mr-2 h-10 w-10 flex items-center justify-center text-slate-600 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                <span className="material-symbols-outlined text-[30px]">restart_alt</span>
            </button>
            <div className="relative w-[200px]">
                <input
                    type="text"
                    value={fieldValue}
                    placeholder={name}
                    onChange={e => {updateValue(e.target.value)}}
                    readOnly={isReadOnly}
                    className="w-full h-[40px] px-3 pr-12 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary read-only:opacity-50 read-only:cursor-not-allowed"
                />
                {field.suffix && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                        {field.suffix}
                    </span>
                )}
            </div>
            <SaveIndicator status={saveStatus} className="ml-2" />
        </div>
    )
}
